import { useState } from 'react';
import { type SavedEntry, clearHistory, removeHistory } from '../lib/storage';
import { buildDeepLinkParams } from '../lib/deepLink';
import { formatYen } from '../lib/format';

interface Props {
  history: SavedEntry[];
  onChange: (next: SavedEntry[]) => void;
}

function timeLabel(ts: number): string {
  const d = new Date(ts);
  const m = `${d.getMonth() + 1}/${d.getDate()}`;
  const t = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  return `${m} ${t}`;
}

function entryDeepLink(e: SavedEntry): string {
  return `?${buildDeepLinkParams({
    position: e.position,
    propertyType: e.values.propertyType,
    floorPlan: e.values.floorPlan,
    buildingYear: e.values.buildingYear,
    area: e.values.area,
    floorArea: e.values.floorArea,
    roadWidthM: e.values.roadWidthM,
    roadDirection: e.values.roadDirection,
  }).toString()}`;
}

export default function HistoryPanel({ history, onChange }: Props) {
  const [open, setOpen] = useState(false);

  if (history.length === 0) return null;

  return (
    <section>
      <h2 className="section-title">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="font-bold text-primary-text hover:text-primary-dark"
          aria-expanded={open}
          aria-controls="history-list"
        >
          この端末で見た{' '}
          <span className="fig">{history.length}</span> 件 {open ? '−' : '+'}
        </button>
      </h2>

      {open && (
        <div id="history-list" className="mt-3">
          <ul className="max-h-64 divide-y divide-rule overflow-y-auto border-y border-rule">
            {history.map((e) => (
              <li key={e.ts} className="flex items-baseline gap-3 py-2 text-[12px]">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-ink-2">
                    {e.result.municipalityName}
                    {e.result.districtName ? ` ${e.result.districtName}` : ''}
                    <span className="ml-1.5 text-ink-3">{e.values.floorPlan}</span>
                  </p>
                  <p className="text-ink-3">
                    <span className="fig text-ink-2">{formatYen(e.result.predictedPrice)}</span>
                    <span className="fig ml-2">{timeLabel(e.ts)}</span>
                  </p>
                </div>
                <a
                  href={entryDeepLink(e)}
                  className="shrink-0 text-ink-2 underline-offset-4 hover:text-primary-text hover:underline"
                >
                  開く
                </a>
                <button
                  type="button"
                  onClick={() => onChange(removeHistory(e.ts))}
                  className="shrink-0 text-ink-3 hover:text-debit"
                  aria-label={`${e.result.municipalityName} の履歴を削除`}
                >
                  削除
                </button>
              </li>
            ))}
          </ul>
          <div className="mt-2 text-right">
            <button
              type="button"
              onClick={() => {
                clearHistory();
                onChange([]);
              }}
              className="text-[12px] text-ink-3 hover:text-debit"
            >
              すべて削除
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
